import React from "react";
import {
  Check,
  ArrowLeft,
  WhatsappLogo,
  Sparkle,
  CreditCard,
  Signature,
  CheckSquare,
  ChatCircleDots,
} from "@phosphor-icons/react";
import StartMiniMockup from "./StartMiniMockups";
import useStartReveal from "./useStartReveal";
import {
  FEATURE_CTA,
  FEATURES,
  FEATURES_SECTION,
  getProfessionContent,
} from "../../data/startPageContent";
import "./StartFeatureRow.css";

const POP_ICONS = { WhatsappLogo, Sparkle, CreditCard, Signature, CheckSquare, ChatCircleDots };

function FeatureRow({ feature, index, services, examples }) {
  const ref = useStartReveal();
  const example = feature.exampleKey ? examples[feature.exampleKey] : null;
  const PopIcon = feature.pop ? POP_ICONS[feature.pop.icon] || Sparkle : null;

  return (
    <article
      ref={ref}
      className={`st-feat st-reveal${index % 2 === 1 ? " st-feat--flip" : ""}`}
      aria-labelledby={`st-feat-${feature.key}`}
    >
      <div className="st-feat-text">
        <h3 id={`st-feat-${feature.key}`} className="st-feat-title">{feature.title}</h3>
        {feature.bullets.length > 0 && (
          <ul className="st-feat-bullets">
            {feature.bullets.map((b) => (
              <li key={b}>
                <span className="st-feat-check" aria-hidden="true">
                  <Check size={12} weight="bold" />
                </span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {example && (
          <p className="st-feat-example">
            <span className="st-feat-example-label">למשל אצלכם:</span> {example}
          </p>
        )}
        {feature.note && (
          <p className="st-feat-note">
            <span className="st-feat-note-dash" aria-hidden="true" />
            {feature.note}
          </p>
        )}
        <a href="#start-form" className="st-btn st-btn--dark st-feat-cta">
          {FEATURE_CTA}
          <ArrowLeft size={18} weight="bold" />
        </a>
      </div>
      <div className="st-feat-visual" aria-hidden="true">
        <div className={`st-feat-stage st-feat-stage--${feature.tone}`}>
          <StartMiniMockup kind={feature.mockup} services={services} />
          {feature.pop && (
            <div className="st-feat-pop">
              <span className="st-feat-pop-icon">
                <PopIcon size={16} weight="fill" />
              </span>
              <span>{feature.pop.text}</span>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

/** Six alternating feature rows (plan §4.3), personalised by profession. */
export default function StartFeatureRows({ profession = null }) {
  const headRef = useStartReveal();
  let content;
  try {
    content = getProfessionContent(profession);
  } catch (error) {
    console.error("[CK-1063] StartFeatureRows personalization failed", {
      errorCode: "CK-1063",
      errorMessage: error?.message,
      profession: profession || null,
      timestamp: new Date().toISOString(),
    });
    content = getProfessionContent(null);
  }

  return (
    <section id="start-features" className="st-section st-features" aria-labelledby="st-features-title">
      <div className="st-container">
        <div className="st-features-head st-reveal" ref={headRef}>
          <p className="st-overline st-center">{FEATURES_SECTION.overline}</p>
          <h2 id="st-features-title" className="st-h2 st-center">{FEATURES_SECTION.title}</h2>
          <p className="st-sub st-center st-features-sub">{FEATURES_SECTION.sub}</p>
        </div>
        <div className="st-features-list">
          {FEATURES.map((f, i) => (
            <FeatureRow
              key={f.key}
              feature={f}
              index={i}
              services={content.calendarServices}
              examples={content.featureExamples}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
